import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Filter,
  Edit2,
  Eye,
  Power,
  GraduationCap,
  Calendar,
  Mail,
  User,
} from "lucide-react";
import { Student } from "@/types";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import Pagination from "@/components/ui/Pagination";
import { formatDate, cn } from "@/lib/utils";
import { api } from "@/lib/api";
import ImpersonateButton from "@/components/impersonation/ImpersonateButton";

const studentDisplayName = (student: Student) => {
  if (student.firstName || student.lastName) {
    return `${student.firstName || ""} ${student.lastName || ""}`.trim();
  }
  return `Student ${student.id.slice(-8)}`;
};

const calculateAge = (dateOfBirth: string) => {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

// Define grades outside the component so it's accessible by both main component and modal
const grades = ["9", "10", "11", "12", "Institution"];

// Backend enum values that the single frontend option "Institution" collapses.
const INSTITUTION_GRADE_ENUMS = ["under_graduate", "diploma", "post_graduate", "doctorate"];

// Map frontend grade values to backend enum values. `originalEnum` is the
// student's grade as it already was in the backend before this edit — for
// "Institution" specifically, several different backend values (diploma,
// under_graduate, post_graduate, doctorate) all display as that one option,
// so without this, saving any unrelated field on such a student would
// silently rewrite their real grade to "under_graduate". If the grade
// wasn't actually changed, this keeps the original value instead.
const mapGradeToEnum = (grade: string, originalEnum?: string): string => {
  if (
    grade === "Institution" &&
    originalEnum &&
    INSTITUTION_GRADE_ENUMS.includes(originalEnum)
  ) {
    return originalEnum;
  }
  const gradeMap: Record<string, string> = {
    "9": "class_9",
    "10": "class_10",
    "11": "class_11",
    "12": "class_12",
    Institution: "under_graduate",
  };
  return gradeMap[grade] || grade;
};

// Map backend enum values to frontend display values
const mapEnumToGrade = (enumValue: string): string => {
  const enumMap: Record<string, string> = {
    class_9: "9",
    class_10: "10",
    class_11: "11",
    class_12: "12",
    under_graduate: "Institution",
    diploma: "Institution",
    post_graduate: "Institution",
    doctorate: "Institution",
  };
  return enumMap[enumValue] || enumValue;
};

const StudentManagement: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [filterGrade] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [error, setError] = useState("");

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  const fetchStudents = async (
    page = 1,
    search = "",
    status = "all",
    grade = "all"
  ) => {
    const params: any = {
      page,
      limit: 12,
    };

    try {
      setLoading(true);

      if (search) params.search = search;
      if (status !== "all") params.isActive = status === "active";
      if (grade !== "all") params.grade = grade;

      console.log("Fetching students with params:", params);

      // Use centralized API call
      const response = await api.get<{ data: Student[]; meta: any }>(
        `/admin/students?${new URLSearchParams(params)}`
      );
      console.log("Student API response:", response);
      console.log("First student data:", response.data?.[0]);
      console.log(
        "Student names in response:",
        response.data?.map(
          (s: Student) => `${s.firstName} ${s.lastName} (${s.id.slice(-8)})`
        )
      );

      // Handle the backend response structure
      setStudents(response.data || []);
      setTotalPages(response.meta?.lastPage || response.meta?.totalPages || 1);
      setTotalStudents(response.meta?.total ?? 0);
      setError("");
    } catch (error) {
      console.error("Failed to fetch students:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        stack: error instanceof Error ? error.stack : undefined,
        params: params,
        url: `/api/admin/students?${new URLSearchParams(params)}`,
      });
      setError(
        `Failed to load students: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      // Mock data for development
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents(currentPage, searchTerm, filterStatus, filterGrade);
  }, [currentPage, searchTerm, filterStatus, filterGrade]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (status: "all" | "active" | "inactive") => {
    setFilterStatus(status);
    setCurrentPage(1);
  };


  const handleAddStudent = () => {
    setSelectedStudent(null);
    setShowAddModal(true);
  };

  const handleEditStudent = async (student: Student) => {
    // Use the student data we already have from the list
    // This avoids the 404 error while the backend route is being fixed
    setSelectedStudent(student);
    setShowEditModal(true);
  };

  const handleViewStudent = async (student: Student) => {
    // Use the student data we already have from the list
    // This avoids the 404 error while the backend route is being fixed
    setSelectedStudent(student);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (student: Student) => {
    const action = student.isActive ? "deactivate" : "activate";

    if (
      !confirm(
        `Are you sure you want to ${action} "${student.firstName} ${student.lastName}"?`
      )
    ) {
      return;
    }

    try {
      // Send the status in the format backend expects
      const newStatus = student.isActive ? "inactive" : "active";

      await api.patch(`/admin/students/${student.id}/status`, {
        status: newStatus,
      });
      // Refresh the list
      fetchStudents(currentPage, searchTerm, filterStatus, filterGrade);
    } catch (error: any) {
      console.error("Failed to toggle student status:", error);
      setError(error.message || "Failed to update student status");
    }
  };

  return (
    <div className="gap-y-6 flex flex-col">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Student Management</h1>
        <button
          onClick={handleAddStudent}
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add Student</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, phone, or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filterStatus}
              onChange={(e) => handleStatusFilterChange(e.target.value as any)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* <select
              value={filterGrade}
              onChange={(e) => handleGradeFilterChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Grades</option>
              {grades.map((grade) => (
                <option key={grade} value={grade}>
                  Grade {grade}
                </option>
              ))}
            </select> */}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : students.length === 0 ? (
          <div className="p-12 text-center">
            <GraduationCap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No students found
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterStatus !== "all" || filterGrade !== "all"
                ? "Try adjusting your search or filter criteria."
                : "Get started by adding your first student."}
            </p>
            {!searchTerm && filterStatus === "all" && filterGrade === "all" && (
              <button
                onClick={handleAddStudent}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 inline-flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add First Student</span>
              </button>
            )}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-gray-200 md:hidden">
              {students.map((student) => (
                <li key={student.id} className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 h-10 w-10">
                      {student.profilePicture ? (
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={student.profilePicture}
                          alt={studentDisplayName(student)}
                        />
                      ) : (
                        <div className="h-10 w-10 bg-indigo-500 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {studentDisplayName(student)}
                      </p>
                      {student.dateOfBirth && (
                        <p className="text-xs text-gray-500">
                          Age: {calculateAge(student.dateOfBirth)}
                        </p>
                      )}
                    </div>
                    <span
                      className={cn(
                        "shrink-0 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full",
                        student.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800",
                      )}
                    >
                      {student.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
                    {student.email && (
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="h-3 w-3 text-gray-400 shrink-0" />
                        {student.email}
                      </span>
                    )}
                    {student.phoneNumber && <span>{student.phoneNumber}</span>}
                    <span>{student.schoolName || "N/A"} · {student.grade ? `Grade ${student.grade}` : "Grade N/A"}</span>
                    <span>{student.totalSessions || 0} sessions</span>
                    <span>Parent: {student.parentId ? "Connected" : "Not linked"}</span>
                  </div>

                  {student.interests && student.interests.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {student.interests.slice(0, 3).map((interest, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                        >
                          {interest}
                        </span>
                      ))}
                      {student.interests.length > 3 && (
                        <span className="text-xs text-gray-500">+{student.interests.length - 3}</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      onClick={() => handleViewStudent(student)}
                      className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"
                      aria-label="View Details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEditStudent(student)}
                      className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50"
                      aria-label="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <ImpersonateButton
                      userId={student.userId}
                      name={studentDisplayName(student)}
                      isActive={student.isActive}
                    />
                    <button
                      onClick={() => handleToggleStatus(student)}
                      className={cn(
                        "p-1.5 rounded-md",
                        student.isActive
                          ? "text-red-600 hover:bg-red-50"
                          : "text-green-600 hover:bg-green-50",
                      )}
                      aria-label={student.isActive ? "Deactivate" : "Activate"}
                    >
                      <Power className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      School/Grade
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Parent Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sessions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interests
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {students.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {student.profilePicture ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={student.profilePicture}
                                alt={`${student.firstName} ${student.lastName}`}
                              />
                            ) : (
                              <div className="h-10 w-10 bg-indigo-500 rounded-full flex items-center justify-center">
                                <User className="h-5 w-5 text-white" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {studentDisplayName(student)}
                            </div>
                            <div className="text-sm text-gray-500">
                              {student.dateOfBirth &&
                                `Age: ${calculateAge(student.dateOfBirth)}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {student.email && (
                            <div className="flex items-center mb-1">
                              <Mail className="h-3 w-3 mr-1 text-gray-400" />
                              <span className="truncate max-w-[130px]" title={student.email}>
                                {student.email}
                              </span>
                            </div>
                          )}
                          {student.phoneNumber && (
                            <div className="flex items-center mb-1">
                              <span className="h-3 w-3 mr-1 text-gray-400">
                                📞
                              </span>
                              <span>{student.phoneNumber}</span>
                            </div>
                          )}
                          {student.dateOfBirth && (
                            <div className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1 text-gray-400" />
                              <span>{formatDate(student.dateOfBirth)}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {student.schoolName || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {student.grade
                            ? `Grade ${student.grade}`
                            : "Grade N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {student.parentName || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {student.parentId ? "Connected" : "Not linked"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {student.totalSessions || 0}
                        </div>
                        <div className="text-sm text-gray-500">
                          {student.lastSessionDate
                            ? formatDate(student.lastSessionDate)
                            : "No sessions"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex px-2 py-1 text-xs font-semibold rounded-full",
                            student.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          )}
                        >
                          {student.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {student.interests && student.interests.length > 0 ? (
                            student.interests
                              .slice(0, 2)
                              .map((interest, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                                >
                                  {interest}
                                </span>
                              ))
                          ) : (
                            <span className="text-sm text-gray-500">None</span>
                          )}
                          {student.interests &&
                            student.interests.length > 2 && (
                              <span className="text-xs text-gray-500">
                                +{student.interests.length - 2}
                              </span>
                            )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewStudent(student)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditStudent(student)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <ImpersonateButton
                            userId={student.userId}
                            name={studentDisplayName(student)}
                            isActive={student.isActive}
                          />
                          <button
                            onClick={() => handleToggleStatus(student)}
                            className={cn(
                              "p-1 rounded",
                              student.isActive
                                ? "text-red-600 hover:text-red-900"
                                : "text-green-600 hover:text-green-900"
                            )}
                            title={student.isActive ? "Deactivate" : "Activate"}
                          >
                            <Power className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                total={totalStudents}
                pageSize={12}
                itemLabel="students"
              />
            </div>
          </>
        )}
      </div>

      {/* Add Student Modal */}
      {showAddModal && (
        <StudentModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchStudents(currentPage, searchTerm, filterStatus, filterGrade);
          }}
          title="Add New Student"
        />
      )}

      {/* Edit Student Modal */}
      {showEditModal && selectedStudent && (
        <StudentModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchStudents(currentPage, searchTerm, filterStatus, filterGrade);
          }}
          student={selectedStudent}
          title="Edit Student"
        />
      )}

      {/* View Student Modal */}
      {showViewModal && selectedStudent && (
        <StudentViewModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          student={selectedStudent}
        />
      )}
    </div>
  );
};

// Student Form Modal Component
interface StudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  student?: Student;
  title: string;
}

const StudentModal: React.FC<StudentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  student,
  title,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    grade: "",
    schoolName: "",
    interests: [] as string[],
    careerGoals: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  // The backend grade enum as it was before this edit — see mapGradeToEnum.
  const [originalGradeEnum, setOriginalGradeEnum] = useState<string | undefined>(undefined);

  // Initialize form data when student is provided (edit mode)
  useEffect(() => {
    if (student) {
      setFormData({
        firstName: student.firstName || "",
        lastName: student.lastName || "",
        email: student.email || "",
        phoneNumber: student.phoneNumber || "",
        dateOfBirth: student.dateOfBirth || "",
        grade: mapEnumToGrade(student.grade || ""), // Map backend enum to frontend display value
        schoolName: student.schoolName || "",
        interests: student.interests || [],
        careerGoals: student.careerGoals || [],
      });
      setOriginalGradeEnum(student.grade || undefined);
    } else {
      setOriginalGradeEnum(undefined);
    }
  }, [student]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError("");

    try {
      // Map frontend form data to backend DTO format - ONLY include backend DTO fields
      const submitData: any = {};

      // Required name field (combine first and last name)
      const fullName = `${formData.firstName || ""} ${
        formData.lastName || ""
      }`.trim();
      if (fullName) {
        submitData.name = fullName;
      }

      // Optional fields - only include if they have valid values
      if (formData.email?.trim() && formData.email.includes("@")) {
        submitData.email = formData.email.trim();
      }

      if (formData.phoneNumber?.trim()) {
        submitData.phoneNumber = formData.phoneNumber.trim();
      }

      if (formData.dateOfBirth) {
        submitData.dateOfBirth = formData.dateOfBirth;
      }

      if (formData.grade) {
        submitData.currentGrade = mapGradeToEnum(formData.grade, originalGradeEnum); // Map frontend grade to backend enum
      }

      if (formData.schoolName?.trim()) {
        submitData.schoolName = formData.schoolName.trim();
      }

      if (formData.interests && formData.interests.length > 0) {
        submitData.interests = formData.interests;
      }

      if (formData.careerGoals && formData.careerGoals.length > 0) {
        submitData.learningGoals = formData.careerGoals; // Map 'careerGoals' to 'learningGoals'
      }

      console.log("Form data:", formData);
      console.log(
        "Grade mapping:",
        formData.grade,
        "->",
        mapGradeToEnum(formData.grade || "", originalGradeEnum)
      );
      console.log("Mapped submit data (backend DTO format):", submitData);
      console.log("Fields being sent:", Object.keys(submitData));

      if (student) {
        // Edit mode - PATCH request
        await api.patch(`/admin/students/${student.id}`, submitData);
      } else {
        // Create mode - POST request
        await api.post("/admin/students", submitData);
      }

      onSuccess();
    } catch (error) {
      console.error(
        `Failed to ${student ? "update" : "create"} student:`,
        error
      );
      setFormError(
        "Network error occurred. Please check your connection and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <div className="text-red-800 text-sm">{formError}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) =>
                    setFormData({ ...formData, phoneNumber: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="+1234567890"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) =>
                    setFormData({ ...formData, dateOfBirth: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grade *
                </label>
                <select
                  required
                  value={formData.grade}
                  onChange={(e) =>
                    setFormData({ ...formData, grade: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select Grade</option>
                  {grades.map((grade) => (
                    <option key={grade} value={grade}>
                      Grade {grade}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Name
                </label>
                <input
                  type="text"
                  value={formData.schoolName}
                  onChange={(e) =>
                    setFormData({ ...formData, schoolName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interests (comma-separated)
              </label>
              <input
                type="text"
                value={formData.interests.join(", ")}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    interests: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter((s) => s),
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Math, Science, Art, Music"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Career Goals (comma-separated)
              </label>
              <input
                type="text"
                value={formData.careerGoals.join(", ")}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    careerGoals: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter((s) => s),
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Engineer, Doctor, Teacher"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting
                  ? "Saving..."
                  : student
                  ? "Update Student"
                  : "Create Student"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Student View Modal Component
interface StudentViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student;
}

const StudentViewModal: React.FC<StudentViewModalProps> = ({
  isOpen,
  onClose,
  student,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 m-auto">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Student Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 bg-indigo-500 rounded-full flex items-center justify-center">
                {student.profilePicture ? (
                  <img
                    src={student.profilePicture}
                    alt={`${student.firstName} ${student.lastName}`}
                    className="h-16 w-16 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-8 w-8 text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {student.firstName} {student.lastName}
                </h3>
                <div className="flex items-center space-x-2 mt-1">
                  <span
                    className={cn(
                      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                      student.isActive
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    )}
                  >
                    {student.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <p className="text-sm text-gray-900">
                  {student.email || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <p className="text-sm text-gray-900">
                  {student.phoneNumber || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <p className="text-sm text-gray-900">
                  {student.dateOfBirth
                    ? `${formatDate(student.dateOfBirth)} (Age: ${calculateAge(
                        student.dateOfBirth
                      )})`
                    : "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grade
                </label>
                <p className="text-sm text-gray-900">
                  {student.grade ? `Grade ${student.grade}` : "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School
                </label>
                <p className="text-sm text-gray-900">
                  {student.schoolName || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent
                </label>
                <p className="text-sm text-gray-900">
                  {student.parentName || "N/A"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Sessions
                </label>
                <p className="text-sm text-gray-900">
                  {student.totalSessions || 0}
                </p>
              </div>
            </div>

            {student.interests && student.interests.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interests
                </label>
                <div className="flex flex-wrap gap-1">
                  {student.interests.map((interest, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {student.careerGoals && student.careerGoals.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Career Goals
                </label>
                <div className="flex flex-wrap gap-1">
                  {student.careerGoals.map((goal, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      {goal}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="text-sm text-gray-500 pt-4 border-t border-gray-200">
              <p>Joined: {formatDate(student.createdAt)}</p>
              <p>Updated: {formatDate(student.updatedAt)}</p>
              {student.lastSessionDate && (
                <p>Last Session: {formatDate(student.lastSessionDate)}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end pt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentManagement;
